import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth, confirmPasswordReset } from 'firebase/auth';
import Logo from '../../components/ui/Logo';
import FloatingInput from '../../components/ui/FloatingInput';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const oobCode = searchParams.get('oobCode');

  useEffect(() => {
    if (!oobCode) {
      setError('Invalid or expired password reset link.');
    }
  }, [oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    setLoading(true);
    try {
      const auth = getAuth();
      await confirmPasswordReset(auth, oobCode!, newPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError('Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-[#fdfbf5] font-['Plus Jakarta Sans']">
      <div className="mt-8 mb-4">
        <Logo />
      </div>
      <div className="w-full max-w-[620px] mx-4 sm:mx-auto rounded-[28px] shadow-[0_4px_32px_0_rgba(0,0,0,0.08)] bg-white p-8 sm:p-8 xs:p-4 flex flex-col items-center font-['Plus Jakarta Sans']" style={{ marginBottom: '40px' }}>
        <h2 className="text-3xl font-bold text-gray-900 mb-2 w-full text-left">Reset Password</h2>
        <p className="text-gray-700 mb-6 w-full text-left text-base">Enter your new password below.</p>
        
        {success ? (
          <div className="text-green-600 text-base mb-8 w-full max-w-[570px]">
            Password reset successful! Redirecting to login...
          </div>
        ) : (
          <form className="w-full flex flex-col items-center" onSubmit={handleSubmit}>
            {error && <div className="text-red-600 text-sm mb-8 w-full max-w-[570px]">{error}</div>}
            
            <FloatingInput
              id="newPassword"
              name="newPassword"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password"
              autoComplete="new-password"
              showPasswordToggle={true}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
            />
            
            <FloatingInput
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              autoComplete="new-password"
              showPasswordToggle={true}
              showPassword={showConfirmPassword}
              setShowPassword={setShowConfirmPassword}
            />

            <button
              type="submit"
              disabled={loading}
              className="w-full max-w-[570px] py-3 rounded-full bg-[#333333] text-white font-semibold text-base shadow-sm hover:bg-gray-800 transition disabled:opacity-60 font-['Plus Jakarta Sans']"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', backgroundColor: '#333333' }}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword; 